import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Sparkles, Mail, MessageCircle, HelpCircle, Send, Check, ChevronDown } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store/authStore'
import client from '../api/client'

const FAQ = [
  {
    q: 'Не приходит письмо с подтверждением email',
    a: 'Проверьте папку «Спам». Если письма нет — нажмите «Отправить ещё раз» на странице регистрации или в профиле.'
  },
  {
    q: 'Почему не работают платные модели?',
    a: 'Платные модели требуют наличия токенов на балансе. Проверьте баланс в профиле. Если токены есть — возможно, проблема с API-ключами провайдера, обратитесь в поддержку.'
  },
  {
    q: 'Как пополнить баланс?',
    a: 'Перейдите в «Профиль» → «Пополнить баланс». Выберите тариф и следуйте инструкциям на странице оплаты.'
  },
  {
    q: 'Можно ли вернуть токены?',
    a: 'Токены являются внутренней расчётной единицей и не подлежат возврату, кроме случаев, предусмотренных законом. По индивидуальным вопросам — пишите в поддержку.'
  },
  {
    q: 'Что делать, если AI отвечает неправильно?',
    a: 'AI-модели могут ошибаться. Попробуйте переформулировать запрос, выбрать другую модель или уточнить контекст. Помните: AI не даёт профессиональных консультаций.'
  },
  {
    q: 'Как удалить аккаунт?',
    a: 'Напишите на support@chat-neuro.ru с вашего зарегистрированного email с запросом на удаление. Аккаунт будет удалён в течение 30 дней.'
  },
  {
    q: 'Бесплатные модели — это навсегда?',
    a: 'Бесплатные модели доступны безлимитно на данный момент. Администрация оставляет за собой право изменить или отменить эту программу.'
  },
]

export default function Support() {
  const { user } = useAuthStore()
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [form, setForm] = useState({ email: '', message: '' })
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.email || !form.message) {
      toast.error('Заполните все поля')
      return
    }
    setSending(true)
    try {
      await client.post('/users/support/send', form)
      setSent(true)
      toast.success('Сообщение отправлено! Мы ответим в ближайшее время')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка отправки')
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      <div className="max-w-3xl mx-auto px-4 pt-8 pb-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link
            to="/"
            className="p-2 rounded-xl glass-hover text-gray-400 hover:text-white transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold gradient-text">Поддержка</h1>
              <p className="text-xs text-gray-500">Мы всегда рядом</p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Contact cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href="mailto:support@chat-neuro.ru"
              className="glass-card p-5 flex items-center gap-4 hover:bg-white/[0.04] transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center group-hover:bg-indigo-500/20 transition-all">
                <Mail className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Email</h3>
                <p className="text-xs text-indigo-400 underline underline-offset-2">support@chat-neuro.ru</p>
                <p className="text-[10px] text-gray-500 mt-0.5">Ответ в течение 24 часов</p>
              </div>
            </a>

            <a
              href="https://t.me/GPT_Chatbotru"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card p-5 flex items-center gap-4 hover:bg-white/[0.04] transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-sky-500/10 flex items-center justify-center group-hover:bg-sky-500/20 transition-all">
                <MessageCircle className="w-6 h-6 text-sky-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Telegram</h3>
                <p className="text-xs text-gray-400">@GPT_Chatbotru</p>
                <p className="text-[10px] text-gray-500 mt-0.5">Новости и обновления</p>
              </div>
            </a>
          </div>

          {/* FAQ */}
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
              <HelpCircle className="w-5 h-5 text-indigo-400" />
              Часто задаваемые вопросы
            </h2>

            <div className="glass-card divide-y divide-white/5">
              {FAQ.map((item, i) => (
                <div key={i}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.02] transition-colors"
                  >
                    <span className="text-sm text-gray-200 font-medium">{item.q}</span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-500 shrink-0 ml-2 transition-transform ${
                        openFaq === i ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaq === i && (
                    <div className="px-4 pb-4 text-xs text-gray-400 leading-relaxed">
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact form */}
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
              <Send className="w-5 h-5 text-indigo-400" />
              Написать нам
            </h2>

            <div className="glass-card p-6">
              {sent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                    <Check className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">Сообщение отправлено!</h3>
                  <p className="text-sm text-gray-400">Мы ответим вам на email в ближайшее время</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Ваш email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="your@email.com"
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Сообщение</label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                      placeholder="Опишите вашу проблему или вопрос..."
                      className="input-field min-h-[120px] resize-y"
                      rows={5}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="gradient-btn px-6 py-3 rounded-xl text-sm flex items-center gap-2 disabled:opacity-40"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? 'Отправка...' : 'Отправить'}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Контактные данные и реквизиты */}
          <div className="glass-card p-6">
            <h2 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4 text-amber-400" />
              Контактные данные и реквизиты
            </h2>
            <div className="text-xs text-gray-400 space-y-1.5 leading-relaxed">
              <p className="text-gray-200 font-medium">Григорьев Игорь Валерьевич</p>
              <p>+375333175553</p>
              <p>213825, Беларусь</p>
              <p>Могилёвская обл. г.Бобруйск</p>
              <p>ул.Горелика, 55-11</p>
              <p className="mt-2">
                УНН МЕ5805018 (741 Инспекция МНС по г.Бобруйску) от 24.07.2024
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}
